import React, { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { Helmet } from "react-helmet";
import Navigation from "../components/navigation";
import Footer from "../components/footer";
import hotelConfig from "../lib/hotel.config";
import { supabase } from "../lib/supabase";
import "./booking.css";

const Booking = () => {
  const location = useLocation();
  const history = useHistory();

  // Ambil params DULU sebelum useState
  const params = new URLSearchParams(location.search);
  const kamarId = params.get("kamar_id") || "";
  const nomorKamar = params.get("nomor_kamar") || "";
  const tipeKamar = params.get("tipe_kamar") || "";
  const hargaMalam = parseInt(params.get("harga_malam") || "0");
  const checkinParam = params.get("checkin") || "";
  const checkoutParam = params.get("checkout") || "";

  const [lang, setLang] = useState("id");
  const [step, setStep] = useState(kamarId ? 1 : 0);
  const [loading, setLoading] = useState(false);
  const [buktiFile, setBuktiFile] = useState(null);
  const [buktiUrl, setBuktiUrl] = useState("");
  const [uploadingBukti, setUploadingBukti] = useState(false);
  const [kodeBooking, setKodeBooking] = useState("");
  const [kamars, setKamars] = useState([]);
  const [loadingKamars, setLoadingKamars] = useState(true);
  const [selectedKamar, setSelectedKamar] = useState(null);
  const [form, setForm] = useState({
    nama_tamu: "",
    email_tamu: "",
    telepon_tamu: "",
    tanggal_checkin: checkinParam,
    tanggal_checkout: checkoutParam,
    jumlah_tamu: 1,
    catatan: "",
    metode_pembayaran: "transfer",
  });

  const jumlahMalam =
    form.tanggal_checkin && form.tanggal_checkout
      ? Math.max(
          1,
          Math.ceil(
            (new Date(form.tanggal_checkout) - new Date(form.tanggal_checkin)) /
              (1000 * 60 * 60 * 24),
          ),
        )
      : 1;

  React.useEffect(() => {
    const fetchKamars = async () => {
      const { data } = await supabase
        .from("kamar")
        .select("*")
        .eq("status", "tersedia")
        .order("tipe");
      if (data) {
        console.log("Tipe kamar dari DB:", data.map(k => k.tipe));
        setKamars(data);
      }
      setLoadingKamars(false);
    };
    fetchKamars();
  }, []);

  const hargaFinal = selectedKamar?.harga_malam || hargaMalam || 0;
  const subtotal = hargaFinal * jumlahMalam;
  const ppn = Math.round(subtotal * 0.11);
  const totalHarga = subtotal + ppn;

  const generateKodeBooking = () => {
    const prefix = "GWO";
    const random = Math.random().toString(36).substring(2, 7).toUpperCase();
    const date = new Date().toISOString().slice(2, 10).replace(/-/g, "");
    return `${prefix}-${date}-${random}`;
  };

  const handleSubmitData = (e) => {
    e.preventDefault();
    if (
      !form.nama_tamu ||
      !form.telepon_tamu ||
      !form.tanggal_checkin ||
      !form.tanggal_checkout
    )
      return;
    setStep(2);
    window.scrollTo(0, 0);
  };

  const handleSubmitBooking = async () => {
    setLoading(true);
    const kode = generateKodeBooking();
    setKodeBooking(kode);

    const kamarFinal = selectedKamar || {
      id: kamarId,
      nomor_kamar: nomorKamar || "WALK-IN",
      tipe: tipeKamar || "Standar",
      harga_malam: hargaMalam,
    };

    const payload = {
      kode_booking: kode,
      nama_tamu: form.nama_tamu,
      email_tamu: form.email_tamu || "",
      telepon_tamu: form.telepon_tamu,
      nomor_kamar: kamarFinal.nomor_kamar,
      tipe_kamar: kamarFinal.tipe,
      tanggal_checkin: form.tanggal_checkin,
      tanggal_checkout: form.tanggal_checkout,
      jumlah_malam: jumlahMalam,
      harga_per_malam: hargaFinal,
      total_harga: totalHarga,
      status: "menunggu",
      catatan: form.catatan || "",
    };

    const { error } = await supabase.from("reservasi").insert(payload);
    setLoading(false);

    if (error) {
      alert("Terjadi kesalahan: " + error.message);
      return;
    }

    setStep(3);
    window.scrollTo(0, 0);
  };

  const handleUploadBukti = async (file) => {
    if (!file) return;
    setUploadingBukti(true);
    const ext = file.name.split(".").pop();
    const fileName = `${kodeBooking}-${Date.now()}.${ext}`;
    const { error } = await supabase.storage
      .from("bukti-transfer")
      .upload(fileName, file);
    if (error) {
      alert("Gagal upload bukti: " + error.message);
      setUploadingBukti(false);
      return;
    }
    const { data } = supabase.storage
      .from("bukti-transfer")
      .getPublicUrl(fileName);
    setBuktiUrl(data.publicUrl);
    await supabase
      .from("reservasi")
      .update({ bukti_transfer: data.publicUrl })
      .eq("kode_booking", kodeBooking);
    setUploadingBukti(false);
  };

  const rekeningBank = [
    {
      bank: "BRI",
      rekening: "1234-5678-9012-3456",
      atas_nama: "Giga Owo Ori Hotel",
    },
    {
      bank: "BNI",
      rekening: "9876-5432-1098-7654",
      atas_nama: "Giga Owo Ori Hotel",
    },
    {
      bank: "Mandiri",
      rekening: "1357-2468-1357-2468",
      atas_nama: "Giga Owo Ori Hotel",
    },
  ];

  const promoStart = new Date("2026-06-21");
  const promoEnd = new Date("2026-07-12T23:59:59");
  const now = new Date();
  const isPromo = now >= promoStart && now <= promoEnd;
  const diskon = 0.25;
  const hitungHargaPromo = (harga) => Math.round(harga * (1 - diskon));

  return (
    <div
      style={{ minHeight: "100vh", backgroundColor: "var(--color-surface)" }}
    >
      <Helmet>
        <title>Booking — {hotelConfig.nama}</title>
      </Helmet>

      <Navigation lang={lang} setLang={setLang} />

      {/* Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #0D8E8C 0%, #0a7a78 100%)",
          padding: "3rem var(--spacing-xl) 2rem",
          textAlign: "center",
          color: "white",
        }}
      >
        <h1
          style={{
            fontFamily: "var(--font-family-heading)",
            fontSize: "2rem",
            marginBottom: "0.5rem",
          }}
        >
          {lang === "id" ? "Pemesanan Kamar" : "Room Booking"}
        </h1>
        <p style={{ opacity: 0.85, fontSize: "14px" }}>{hotelConfig.nama}</p>

        {/* Step indicator */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "0",
            marginTop: "2rem",
            maxWidth: "400px",
            margin: "2rem auto 0",
          }}
        >
          {[
            { n: 0, label: lang === "id" ? "Pilih Kamar" : "Select Room" },
            { n: 1, label: lang === "id" ? "Data Tamu" : "Guest Info" },
            { n: 2, label: lang === "id" ? "Pembayaran" : "Payment" },
            { n: 3, label: lang === "id" ? "Konfirmasi" : "Confirmed" },
          ].map(({ n, label }, i) => (
            <div
              key={n}
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", width: "100%" }}
              >
                {i > 0 && (
                  <div
                    style={{
                      flex: 1,
                      height: "2px",
                      backgroundColor:
                        step > i - 1 ? "white" : "rgba(255,255,255,0.3)",
                    }}
                  />
                )}
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "50%",
                    backgroundColor:
                      step >= n ? "white" : "rgba(255,255,255,0.2)",
                    color: step >= n ? "#0D8E8C" : "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "700",
                    fontSize: "13px",
                    flexShrink: 0,
                  }}
                >
                  {step > n ? "✓" : n + 1}
                </div>
                {i < 3 && (
                  <div
                    style={{
                      flex: 1,
                      height: "2px",
                      backgroundColor:
                        step > n ? "white" : "rgba(255,255,255,0.3)",
                    }}
                  />
                )}
              </div>
              <span
                style={{
                  fontSize: "10px",
                  marginTop: "6px",
                  opacity: step >= n ? 1 : 0.6,
                }}
              >
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
          padding: "2rem var(--spacing-xl)",
        }}
      >
        {/* Ringkasan Kamar */}
        {tipeKamar && step < 3 && (
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              padding: "1.25rem 1.5rem",
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              border: "1px solid #f0f0f0",
              marginBottom: "1.5rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "1rem",
            }}
          >
            <div>
              <div
                style={{ fontSize: "12px", color: "#888", marginBottom: "2px" }}
              >
                {lang === "id" ? "Kamar yang dipesan" : "Selected Room"}
              </div>
              <div
                style={{
                  fontWeight: "700",
                  color: "#1a1a1a",
                  fontSize: "16px",
                }}
              >
                {tipeKamar} — {lang === "id" ? "Kamar" : "Room"} {nomorKamar}
              </div>
              <div
                style={{ fontSize: "13px", color: "#666", marginTop: "2px" }}
              >
                {jumlahMalam} {lang === "id" ? "malam" : "night(s)"} × IDR{" "}
                {hargaMalam.toLocaleString("id-ID")}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: "11px", color: "#888" }}>
                {lang === "id" ? "Total" : "Total"}
              </div>
              <div
                style={{
                  fontSize: "22px",
                  fontWeight: "800",
                  color: "#0D8E8C",
                }}
              >
                IDR {totalHarga.toLocaleString("id-ID")}
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 0: Pilih Kamar ── */}
        {step === 0 && (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            <div
              style={{
                background: "white",
                borderRadius: "20px",
                padding: "2rem",
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              }}
            >
              <h2
                style={{
                  fontFamily: "var(--font-family-heading)",
                  fontSize: "1.4rem",
                  marginBottom: "0.5rem",
                  color: "#1a1a1a",
                }}
              >
                {lang === "id" ? "Pilih Kamar" : "Select Room"}
              </h2>
              <p
                style={{
                  fontSize: "13px",
                  color: "#888",
                  marginBottom: "1.5rem",
                }}
              >
                {lang === "id"
                  ? "Pilih kamar yang sesuai dengan kebutuhan Anda"
                  : "Choose the room that suits your needs"}
              </p>

              {loadingKamars ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "2rem",
                    color: "#888",
                  }}
                >
                  {lang === "id" ? "Memuat kamar..." : "Loading rooms..."}
                </div>
              ) : kamars.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "2rem",
                    color: "#888",
                  }}
                >
                  {lang === "id"
                    ? "Tidak ada kamar tersedia"
                    : "No rooms available"}
                </div>
              ) : (
                (() => {
                  const tipeOrder = [
                    "Superior Single",
                    "Deluxe Twin",
                    "Deluxe Single",
                    "Executive",
                    "President Suite",
                  ];
                  const grouped = {};
                  kamars.forEach((k) => {
                    const tipeKey = k.tipe?.trim();
                    if (!grouped[tipeKey]) grouped[tipeKey] = [];
                    grouped[tipeKey].push(k);
                  });
                  const semuaTipe = Object.keys(grouped);
                  const tipeList = [
                    ...tipeOrder.filter((t) => grouped[t]),
                    ...semuaTipe.filter((t) => !tipeOrder.includes(t)),
                  ];

                  return tipeList.map((tipe) => {
                    const kamarList = grouped[tipe];
                    const allFotos = kamarList
                      .flatMap((k) => k.foto_urls || [])
                      .filter(Boolean);
                    const fotoUtama =
                      allFotos[0] ||
                      "https://images.pexels.com/photos/2506990/pexels-photo-2506990.jpeg?auto=compress&cs=tinysrgb&w=1500";
                    const hargaMulai = Math.min(
                      ...kamarList.map((k) => k.harga_malam),
                    );

                    return (
                      <div
                        key={tipe}
                        style={{
                          borderRadius: "16px",
                          overflow: "hidden",
                          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                          border: "1px solid #f0f0f0",
                          marginBottom: "1rem",
                        }}
                      >
                        {/* Card Tipe */}
                        <div
                          style={{
                            background: "white",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <div
                            style={{
                              width: "100%",
                              overflow: "hidden",
                              height: "200px",
                            }}
                          >
                            <img
                              src={fotoUtama}
                              alt={tipe}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </div>
                          <div
                            style={{
                              flex: 1,
                              padding: "1.25rem",
                              display: "flex",
                              flexDirection: "column",
                              gap: "0.4rem",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.75rem",
                                flexWrap: "wrap",
                              }}
                            >
                              <h3
                                style={{
                                  fontFamily: "var(--font-family-heading)",
                                  fontSize: "1.1rem",
                                  fontWeight: "700",
                                  color: "#1a1a1a",
                                  margin: 0,
                                }}
                              >
                                {tipe}
                              </h3>
                              <span
                                style={{
                                  fontSize: "11px",
                                  fontWeight: "600",
                                  color: "#0D8E8C",
                                  backgroundColor: "#e6f7f7",
                                  padding: "3px 10px",
                                  borderRadius: "20px",
                                  border: "1px solid #b2dfdb",
                                }}
                              >
                                {kamarList.length}{" "}
                                {lang === "id"
                                  ? "kamar tersedia"
                                  : "rooms available"}
                              </span>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                gap: "1rem",
                                flexWrap: "wrap",
                              }}
                            >
                              <span style={{ fontSize: "12px", color: "#555" }}>
                                👤{" "}
                                {lang === "id"
                                  ? `Maks. ${kamarList[0].kapasitas} tamu`
                                  : `Max. ${kamarList[0].kapasitas} guests`}
                              </span>
                              <span style={{ fontSize: "12px", color: "#555" }}>
                                🏢{" "}
                                {lang === "id"
                                  ? `Lantai ${kamarList[0].lantai}`
                                  : `Floor ${kamarList[0].lantai}`}
                              </span>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "5px",
                              }}
                            >
                              {(kamarList[0].fasilitas || [])
                                .slice(0, 5)
                                .map((tag, j) => (
                                  <span
                                    key={j}
                                    style={{
                                      fontSize: "10px",
                                      padding: "2px 8px",
                                      backgroundColor: "#f0fafa",
                                      color: "#0D8E8C",
                                      borderRadius: "20px",
                                      border: "1px solid #b2dfdb",
                                    }}
                                  >
                                    ✓ {tag}
                                  </span>
                                ))}
                              {(kamarList[0].fasilitas || []).length > 5 && (
                                <span
                                  style={{
                                    fontSize: "10px",
                                    padding: "2px 8px",
                                    backgroundColor: "#f5f5f5",
                                    color: "#888",
                                    borderRadius: "20px",
                                  }}
                                >
                                  +{(kamarList[0].fasilitas || []).length - 5}{" "}
                                  lainnya
                                </span>
                              )}
                            </div>
                          </div>
                          <div
                            style={{
                              width: "100%",
                              borderTop: "1px solid #f0f0f0",
                              padding: "1.25rem",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "space-between",
                              backgroundColor: "#fafffe",
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  fontSize: "10px",
                                  color: "#888",
                                  marginBottom: "3px",
                                }}
                              >
                                {lang === "id" ? "Mulai dari" : "From"}
                              </div>
                              {isPromo ? (
                                <>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      fontSize: "10px",
                                      fontWeight: "700",
                                      color: "white",
                                      backgroundColor: "#dc2626",
                                      padding: "2px 7px",
                                      borderRadius: "20px",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    🎉 Promo 25%
                                  </span>
                                  <div
                                    style={{
                                      fontSize: "12px",
                                      color: "#aaa",
                                      textDecoration: "line-through",
                                    }}
                                  >
                                    IDR {hargaMulai.toLocaleString("id-ID")}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "18px",
                                      fontWeight: "800",
                                      color: "#0D8E8C",
                                      lineHeight: "1.2",
                                    }}
                                  >
                                    IDR {hitungHargaPromo(hargaMulai).toLocaleString("id-ID")}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "10px",
                                      color: "#dc2626",
                                      marginTop: "2px",
                                      fontWeight: "600",
                                    }}
                                  >
                                    s.d. 12 Juli 2026
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div
                                    style={{
                                      fontSize: "18px",
                                      fontWeight: "800",
                                      color: "#0D8E8C",
                                      lineHeight: "1.2",
                                    }}
                                  >
                                    IDR {hargaMulai.toLocaleString("id-ID")}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "10px",
                                      color: "#888",
                                      marginTop: "2px",
                                    }}
                                  >
                                    {lang === "id" ? "per malam" : "per night"}
                                  </div>
                                </>
                              )}
                            </div>
                            <button
                              onClick={() => {
                                setSelectedKamar(kamarList[0]);
                                setStep(1);
                                window.scrollTo(0, 0);
                              }}
                              style={{
                                width: "100%",
                                backgroundColor: "#0D8E8C",
                                color: "white",
                                border: "none",
                                borderRadius: "8px",
                                padding: "10px",
                                fontSize: "13px",
                                fontWeight: "700",
                                cursor: "pointer",
                                marginTop: "0.75rem",
                              }}
                            >
                              Pesan Kamar
                            </button>
                          </div>
                        </div>

                      </div>
                    );
                  });
                })()
              )}
            </div>

            <button
              onClick={() => {
                if (!selectedKamar) {
                  alert(
                    lang === "id"
                      ? "Silakan pilih kamar terlebih dahulu"
                      : "Please select a room first",
                  );
                  return;
                }
                setStep(1);
                window.scrollTo(0, 0);
              }}
              style={{
                width: "100%",
                padding: "14px",
                borderRadius: "12px",
                backgroundColor: selectedKamar ? "#0D8E8C" : "#ccc",
                color: "white",
                border: "none",
                fontSize: "15px",
                fontWeight: "700",
                cursor: selectedKamar ? "pointer" : "not-allowed",
              }}
            >
              {lang === "id"
                ? "Lanjut ke Data Tamu →"
                : "Continue to Guest Info →"}
            </button>
          </div>
        )}

        {step === 1 && (
          <div
            style={{
              background: "white",
              borderRadius: "20px",
              padding: "2rem",
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
            }}
          >
            <h2
              style={{
                fontFamily: "var(--font-family-heading)",
                fontSize: "1.4rem",
                marginBottom: "1.5rem",
                color: "#1a1a1a",
              }}
            >
              {lang === "id" ? "Data Tamu" : "Guest Information"}
            </h2>
            <form
              onSubmit={handleSubmitData}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1.25rem",
              }}
            >
              {/* Nama */}
              <div>
                <label
                  style={{
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#555",
                    display: "block",
                    marginBottom: "6px",
                  }}
                >
                  {lang === "id" ? "Nama Lengkap" : "Full Name"} *
                </label>
                <input
                  type="text"
                  required
                  value={form.nama_tamu}
                  onChange={(e) =>
                    setForm({ ...form, nama_tamu: e.target.value })
                  }
                  placeholder={
                    lang === "id" ? "Masukkan nama lengkap" : "Enter full name"
                  }
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: "10px",
                    border: "1px solid #e0e0e0",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    outline: "none",
                  }}
                />
              </div>

              {/* Email & Telepon */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "1rem",
                }}
              >
                <div>
                  <label
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#555",
                      display: "block",
                      marginBottom: "6px",
                    }}
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    value={form.email_tamu}
                    onChange={(e) =>
                      setForm({ ...form, email_tamu: e.target.value })
                    }
                    placeholder="email@contoh.com"
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      borderRadius: "10px",
                      border: "1px solid #e0e0e0",
                      fontSize: "14px",
                      boxSizing: "border-box",
                      outline: "none",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#555",
                      display: "block",
                      marginBottom: "6px",
                    }}
                  >
                    {lang === "id" ? "No. WhatsApp" : "WhatsApp No."} *
                  </label>
                  <input
                    type="tel"
                    required
                    value={form.telepon_tamu}
                    onChange={(e) =>
                      setForm({ ...form, telepon_tamu: e.target.value })
                    }
                    placeholder="08xxxxxxxxxx"
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      borderRadius: "10px",
                      border: "1px solid #e0e0e0",
                      fontSize: "14px",
                      boxSizing: "border-box",
                      outline: "none",
                    }}
                  />
                </div>
              </div>

              {/* Check-in & Check-out */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "1rem",
                }}
              >
                <div>
                  <label
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#555",
                      display: "block",
                      marginBottom: "6px",
                    }}
                  >
                    {lang === "id" ? "Tanggal Check-in" : "Check-in Date"} *
                  </label>
                  <input
                    type="date"
                    required
                    value={form.tanggal_checkin}
                    min={new Date().toISOString().split("T")[0]}
                    onChange={(e) =>
                      setForm({ ...form, tanggal_checkin: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      borderRadius: "10px",
                      border: "1px solid #e0e0e0",
                      fontSize: "14px",
                      boxSizing: "border-box",
                      outline: "none",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#555",
                      display: "block",
                      marginBottom: "6px",
                    }}
                  >
                    {lang === "id" ? "Tanggal Check-out" : "Check-out Date"} *
                  </label>
                  <input
                    type="date"
                    required
                    value={form.tanggal_checkout}
                    min={
                      form.tanggal_checkin ||
                      new Date().toISOString().split("T")[0]
                    }
                    onChange={(e) =>
                      setForm({ ...form, tanggal_checkout: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      borderRadius: "10px",
                      border: "1px solid #e0e0e0",
                      fontSize: "14px",
                      boxSizing: "border-box",
                      outline: "none",
                    }}
                  />
                </div>
              </div>

              {/* Jumlah Tamu */}
              <div>
                <label
                  style={{
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#555",
                    display: "block",
                    marginBottom: "6px",
                  }}
                >
                  {lang === "id" ? "Jumlah Tamu" : "Number of Guests"} *
                </label>
                <select
                  value={form.jumlah_tamu}
                  onChange={(e) =>
                    setForm({ ...form, jumlah_tamu: parseInt(e.target.value) })
                  }
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: "10px",
                    border: "1px solid #e0e0e0",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    outline: "none",
                    backgroundColor: "white",
                  }}
                >
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <option key={n} value={n}>
                      {n} {lang === "id" ? "Tamu" : "Guest(s)"}
                    </option>
                  ))}
                </select>
              </div>

              {/* Catatan */}
              <div>
                <label
                  style={{
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#555",
                    display: "block",
                    marginBottom: "6px",
                  }}
                >
                  {lang === "id" ? "Catatan Khusus" : "Special Requests"} (
                  {lang === "id" ? "opsional" : "optional"})
                </label>
                <textarea
                  value={form.catatan}
                  onChange={(e) =>
                    setForm({ ...form, catatan: e.target.value })
                  }
                  placeholder={
                    lang === "id"
                      ? "Permintaan khusus, alergi, dll..."
                      : "Special requests, allergies, etc..."
                  }
                  rows={3}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: "10px",
                    border: "1px solid #e0e0e0",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    outline: "none",
                    resize: "vertical",
                    fontFamily: "var(--font-family-body)",
                  }}
                />
              </div>

              <button
                type="submit"
                style={{
                  width: "100%",
                  padding: "14px",
                  borderRadius: "12px",
                  backgroundColor: "#0D8E8C",
                  color: "white",
                  border: "none",
                  fontSize: "15px",
                  fontWeight: "700",
                  cursor: "pointer",
                }}
              >
                {lang === "id"
                  ? "Lanjut ke Pembayaran →"
                  : "Continue to Payment →"}
              </button>
            </form>
          </div>
        )}

        {/* ── STEP 2: Pembayaran ── */}
        {step === 2 && (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
          >
            {/* Pilih Metode */}
            <div
              style={{
                background: "white",
                borderRadius: "20px",
                padding: "2rem",
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              }}
            >
              <h2
                style={{
                  fontFamily: "var(--font-family-heading)",
                  fontSize: "1.4rem",
                  marginBottom: "1.5rem",
                  color: "#1a1a1a",
                }}
              >
                {lang === "id" ? "Metode Pembayaran" : "Payment Method"}
              </h2>

              {/* Transfer Bank */}
              <div
                onClick={() =>
                  setForm({ ...form, metode_pembayaran: "transfer" })
                }
                style={{
                  border: `2px solid ${form.metode_pembayaran === "transfer" ? "#0D8E8C" : "#e0e0e0"}`,
                  borderRadius: "12px",
                  padding: "1.25rem",
                  marginBottom: "1rem",
                  cursor: "pointer",
                  backgroundColor:
                    form.metode_pembayaran === "transfer" ? "#f0fafa" : "white",
                  transition: "all 0.2s",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      border: `2px solid ${form.metode_pembayaran === "transfer" ? "#0D8E8C" : "#ccc"}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {form.metode_pembayaran === "transfer" && (
                      <div
                        style={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                          backgroundColor: "#0D8E8C",
                        }}
                      />
                    )}
                  </div>
                  <div>
                    <div
                      style={{
                        fontWeight: "700",
                        color: "#1a1a1a",
                        fontSize: "15px",
                      }}
                    >
                      🏦{" "}
                      {lang === "id" ? "Transfer Bank Manual" : "Bank Transfer"}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#888",
                        marginTop: "2px",
                      }}
                    >
                      {lang === "id"
                        ? "Transfer ke rekening hotel, konfirmasi via WhatsApp"
                        : "Transfer to hotel account, confirm via WhatsApp"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Gateway - Placeholder */}
              <div
                style={{
                  border: "2px solid #e0e0e0",
                  borderRadius: "12px",
                  padding: "1.25rem",
                  cursor: "not-allowed",
                  backgroundColor: "#fafafa",
                  opacity: 0.6,
                  position: "relative",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      border: "2px solid #ccc",
                      flexShrink: 0,
                    }}
                  />
                  <div>
                    <div
                      style={{
                        fontWeight: "700",
                        color: "#1a1a1a",
                        fontSize: "15px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      💳{" "}
                      {lang === "id"
                        ? "Kartu Kredit / Transfer Instan"
                        : "Credit Card / Instant Transfer"}
                      <span
                        style={{
                          fontSize: "10px",
                          backgroundColor: "#D4AF00",
                          color: "white",
                          padding: "2px 8px",
                          borderRadius: "20px",
                          fontWeight: "600",
                        }}
                      >
                        {lang === "id" ? "Segera Hadir" : "Coming Soon"}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#888",
                        marginTop: "2px",
                      }}
                    >
                      Midtrans — Visa, Mastercard, GoPay, OVO, DANA
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Info Transfer Bank */}
            {form.metode_pembayaran === "transfer" && (
              <div
                style={{
                  background: "white",
                  borderRadius: "20px",
                  padding: "2rem",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                }}
              >
                <h3
                  style={{
                    fontFamily: "var(--font-family-heading)",
                    fontSize: "1.1rem",
                    marginBottom: "1rem",
                    color: "#1a1a1a",
                  }}
                >
                  {lang === "id" ? "Rekening Tujuan" : "Bank Account Details"}
                </h3>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.75rem",
                  }}
                >
                  {rekeningBank.map((r, i) => (
                    <div
                      key={i}
                      style={{
                        padding: "1rem",
                        borderRadius: "10px",
                        backgroundColor: "#f8f9fa",
                        border: "1px solid #e9ecef",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontWeight: "700",
                              color: "#1a1a1a",
                              fontSize: "15px",
                            }}
                          >
                            {r.bank}
                          </div>
                          <div
                            style={{
                              fontSize: "16px",
                              fontWeight: "600",
                              color: "#0D8E8C",
                              letterSpacing: "1px",
                              marginTop: "2px",
                            }}
                          >
                            {r.rekening}
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#888",
                              marginTop: "2px",
                            }}
                          >
                            a.n. {r.atas_nama}
                          </div>
                        </div>
                        <button
                          onClick={() =>
                            navigator.clipboard.writeText(
                              r.rekening.replace(/-/g, ""),
                            )
                          }
                          style={{
                            padding: "6px 14px",
                            borderRadius: "8px",
                            border: "1px solid #0D8E8C",
                            backgroundColor: "transparent",
                            color: "#0D8E8C",
                            fontSize: "12px",
                            cursor: "pointer",
                            fontWeight: "600",
                          }}
                        >
                          {lang === "id" ? "Salin" : "Copy"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div
                  style={{
                    marginTop: "1rem",
                    padding: "12px",
                    borderRadius: "10px",
                    backgroundColor: "#fff8e1",
                    border: "1px solid #ffe082",
                  }}
                >
                  <p
                    style={{
                      fontSize: "13px",
                      color: "#f57f17",
                      margin: 0,
                      lineHeight: "1.6",
                    }}
                  >
                    ⚠️{" "}
                    {lang === "id"
                      ? `Harap transfer sejumlah IDR ${totalHarga.toLocaleString("id-ID")} dan kirim bukti transfer ke WhatsApp kami di ${hotelConfig.telepon}`
                      : `Please transfer IDR ${totalHarga.toLocaleString("id-ID")} and send proof of transfer to our WhatsApp at ${hotelConfig.telepon}`}
                  </p>
                </div>
              </div>
            )}

            {/* Ringkasan Final */}
            <div
              style={{
                background: "white",
                borderRadius: "20px",
                padding: "2rem",
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              }}
            >
              <h3
                style={{
                  fontFamily: "var(--font-family-heading)",
                  fontSize: "1.1rem",
                  marginBottom: "1rem",
                  color: "#1a1a1a",
                }}
              >
                {lang === "id" ? "Ringkasan Pesanan" : "Order Summary"}
              </h3>
              {[
                {
                  label: lang === "id" ? "Nama" : "Name",
                  value: form.nama_tamu,
                },
                { label: "WhatsApp", value: form.telepon_tamu },
                {
                  label: lang === "id" ? "Check-in" : "Check-in",
                  value: form.tanggal_checkin,
                },
                {
                  label: lang === "id" ? "Check-out" : "Check-out",
                  value: form.tanggal_checkout,
                },
                {
                  label: lang === "id" ? "Durasi" : "Duration",
                  value: `${jumlahMalam} ${lang === "id" ? "malam" : "night(s)"}`,
                },
                {
                  label: lang === "id" ? "Jumlah Tamu" : "Guests",
                  value: `${form.jumlah_tamu} ${lang === "id" ? "tamu" : "guest(s)"}`,
                },
                {
                  label: lang === "id" ? "Subtotal" : "Subtotal",
                  value: `IDR ${subtotal.toLocaleString("id-ID")}`,
                },
                {
                  label: "PPn 11%",
                  value: `IDR ${ppn.toLocaleString("id-ID")}`,
                },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "8px 0",
                    borderBottom: "1px solid #f0f0f0",
                    fontSize: "14px",
                  }}
                >
                  <span style={{ color: "#888" }}>{label}</span>
                  <span style={{ fontWeight: "600", color: "#1a1a1a" }}>
                    {value}
                  </span>
                </div>
              ))}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "12px 0",
                  fontSize: "16px",
                  fontWeight: "700",
                }}
              >
                <span>Total</span>
                <span style={{ color: "#0D8E8C" }}>
                  IDR {totalHarga.toLocaleString("id-ID")}
                </span>
              </div>
            </div>

            <div style={{ display: "flex", gap: "1rem" }}>
              <button
                onClick={() => {
                  setStep(1);
                  window.scrollTo(0, 0);
                }}
                style={{
                  flex: 1,
                  padding: "14px",
                  borderRadius: "12px",
                  border: "1px solid #e0e0e0",
                  backgroundColor: "white",
                  fontSize: "15px",
                  fontWeight: "600",
                  cursor: "pointer",
                  color: "#666",
                }}
              >
                ← {lang === "id" ? "Kembali" : "Back"}
              </button>
              <button
                onClick={handleSubmitBooking}
                disabled={loading}
                style={{
                  flex: 2,
                  padding: "14px",
                  borderRadius: "12px",
                  backgroundColor: loading ? "#888" : "#0D8E8C",
                  color: "white",
                  border: "none",
                  fontSize: "15px",
                  fontWeight: "700",
                  cursor: loading ? "not-allowed" : "pointer",
                }}
              >
                {loading
                  ? lang === "id"
                    ? "Memproses..."
                    : "Processing..."
                  : lang === "id"
                    ? "Konfirmasi Pemesanan ✓"
                    : "Confirm Booking ✓"}
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3: Konfirmasi ── */}
        {step === 3 && (
          <div
            style={{
              background: "white",
              borderRadius: "20px",
              padding: "2.5rem",
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "64px", marginBottom: "1rem" }}>✅</div>
            <h2
              style={{
                fontFamily: "var(--font-family-heading)",
                fontSize: "1.8rem",
                color: "#1a1a1a",
                marginBottom: "0.5rem",
              }}
            >
              {lang === "id" ? "Pemesanan Berhasil!" : "Booking Confirmed!"}
            </h2>
            <p
              style={{ color: "#666", fontSize: "14px", marginBottom: "2rem" }}
            >
              {lang === "id"
                ? "Terima kasih telah memesan di"
                : "Thank you for booking at"}{" "}
              {hotelConfig.nama}
            </p>

            {/* Kode Booking */}
            <div
              style={{
                backgroundColor: "#f0fafa",
                borderRadius: "16px",
                padding: "1.5rem",
                marginBottom: "2rem",
                border: "2px dashed #0D8E8C",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  color: "#888",
                  marginBottom: "8px",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                }}
              >
                {lang === "id" ? "Kode Booking Anda" : "Your Booking Code"}
              </div>
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: "800",
                  color: "#0D8E8C",
                  letterSpacing: "2px",
                  fontFamily: "monospace",
                }}
              >
                {kodeBooking}
              </div>
              <div
                style={{ fontSize: "12px", color: "#888", marginTop: "8px" }}
              >
                {lang === "id"
                  ? "Simpan kode ini untuk konfirmasi pembayaran"
                  : "Save this code for payment confirmation"}
              </div>
            </div>

            {/* Info selanjutnya */}
            <div
              style={{
                backgroundColor: "#fff8e1",
                borderRadius: "12px",
                padding: "1.25rem",
                marginBottom: "2rem",
                textAlign: "left",
              }}
            >
              <h4
                style={{
                  fontWeight: "700",
                  color: "#f57f17",
                  marginBottom: "0.75rem",
                  fontSize: "14px",
                }}
              >
                {lang === "id" ? "📋 Langkah Selanjutnya:" : "📋 Next Steps:"}
              </h4>
              <ol
                style={{
                  margin: 0,
                  padding: "0 0 0 1.25rem",
                  fontSize: "13px",
                  color: "#555",
                  lineHeight: "2",
                }}
              >
                <li>
                  {lang === "id"
                    ? `Transfer sejumlah IDR ${totalHarga.toLocaleString("id-ID")} ke rekening hotel`
                    : `Transfer IDR ${totalHarga.toLocaleString("id-ID")} to hotel account`}
                </li>
                <li>
                  {lang === "id"
                    ? "Kirim bukti transfer + kode booking ke WhatsApp hotel"
                    : "Send transfer proof + booking code to hotel WhatsApp"}
                </li>
                <li>
                  {lang === "id"
                    ? "Tim kami akan konfirmasi dalam 1x24 jam"
                    : "Our team will confirm within 24 hours"}
                </li>
              </ol>
            </div>

            {/* Upload Bukti Transfer */}
            {!buktiUrl ? (
              <div
                style={{
                  marginBottom: "1.5rem",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "16px",
                  padding: "1.5rem",
                  border: "2px dashed #dee2e6",
                }}
              >
                <h4
                  style={{
                    fontWeight: "700",
                    color: "#1a1a1a",
                    marginBottom: "0.5rem",
                    fontSize: "15px",
                  }}
                >
                  📎{" "}
                  {lang === "id"
                    ? "Upload Bukti Transfer"
                    : "Upload Transfer Proof"}
                </h4>
                <p
                  style={{
                    fontSize: "13px",
                    color: "#888",
                    marginBottom: "1rem",
                  }}
                >
                  {lang === "id"
                    ? "Upload foto/screenshot bukti transfer agar pesanan Anda bisa segera diproses"
                    : "Upload your transfer proof so we can process your booking faster"}
                </p>
                {buktiFile ? (
                  <div style={{ marginBottom: "1rem" }}>
                    <img
                      src={URL.createObjectURL(buktiFile)}
                      alt="Bukti Transfer"
                      style={{
                        width: "100%",
                        maxHeight: "200px",
                        objectFit: "contain",
                        borderRadius: "10px",
                        border: "1px solid #e0e0e0",
                      }}
                    />
                    <p
                      style={{
                        fontSize: "12px",
                        color: "#888",
                        marginTop: "6px",
                      }}
                    >
                      {buktiFile.name}
                    </p>
                  </div>
                ) : null}
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    padding: "12px",
                    borderRadius: "10px",
                    border: "1px solid #0D8E8C",
                    color: "#0D8E8C",
                    fontWeight: "600",
                    fontSize: "14px",
                    cursor: "pointer",
                    backgroundColor: "white",
                    marginBottom: "1rem",
                  }}
                >
                  📷{" "}
                  {buktiFile
                    ? lang === "id"
                      ? "Ganti Foto"
                      : "Change Photo"
                    : lang === "id"
                      ? "Pilih Foto Bukti Transfer"
                      : "Select Transfer Proof Photo"}
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) =>
                      e.target.files && setBuktiFile(e.target.files[0])
                    }
                  />
                </label>
                {buktiFile && (
                  <button
                    onClick={() => handleUploadBukti(buktiFile)}
                    disabled={uploadingBukti}
                    style={{
                      width: "100%",
                      padding: "12px",
                      borderRadius: "10px",
                      backgroundColor: uploadingBukti ? "#888" : "#0D8E8C",
                      color: "white",
                      border: "none",
                      fontWeight: "700",
                      fontSize: "14px",
                      cursor: uploadingBukti ? "not-allowed" : "pointer",
                    }}
                  >
                    {uploadingBukti
                      ? lang === "id"
                        ? "Mengupload..."
                        : "Uploading..."
                      : lang === "id"
                        ? "⬆️ Upload Bukti Transfer"
                        : "⬆️ Upload Proof"}
                  </button>
                )}
              </div>
            ) : (
              <div
                style={{
                  marginBottom: "1.5rem",
                  backgroundColor: "#f0fff4",
                  borderRadius: "16px",
                  padding: "1.25rem",
                  border: "1px solid #86efac",
                }}
              >
                <p
                  style={{
                    color: "#166534",
                    fontWeight: "600",
                    fontSize: "14px",
                    margin: 0,
                  }}
                >
                  ✅{" "}
                  {lang === "id"
                    ? "Bukti transfer berhasil diupload! Tim kami akan memverifikasi segera."
                    : "Transfer proof uploaded! Our team will verify shortly."}
                </p>
              </div>
            )}

            {/* Tombol WA — hanya muncul setelah upload bukti */}
            {buktiUrl && (
              <button
                onClick={() => {
                  const msg =
                    lang === "id"
                      ? `Halo, saya ingin konfirmasi pembayaran untuk booking ${kodeBooking} atas nama ${form.nama_tamu}. Check-in: ${form.tanggal_checkin}, Check-out: ${form.tanggal_checkout}. Total: IDR ${totalHarga.toLocaleString("id-ID")}. Bukti transfer sudah saya upload.`
                      : `Hello, I want to confirm payment for booking ${kodeBooking} under ${form.nama_tamu}. Check-in: ${form.tanggal_checkin}, Check-out: ${form.tanggal_checkout}. Total: IDR ${totalHarga.toLocaleString("id-ID")}. Transfer proof has been uploaded.`;
                  window.open(
                    `https://wa.me/${hotelConfig.whatsapp}?text=${encodeURIComponent(msg)}`,
                    "_blank",
                  );
                }}
                style={{
                  backgroundColor: "#25D366",
                  color: "white",
                  padding: "14px 32px",
                  borderRadius: "12px",
                  fontWeight: "700",
                  fontSize: "15px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  border: "none",
                  cursor: "pointer",
                  marginBottom: "1rem",
                  width: "100%",
                  justifyContent: "center",
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M18.497 4.409a10 10 0 0 1-10.36 16.828l-.223-.098l-4.759.849l-.11.011a1 1 0 0 1-.11 0l-.102-.013l-.108-.024l-.105-.037l-.099-.047l-.093-.058l-.014-.011l-.012-.007l-.086-.073l-.077-.08l-.067-.088l-.056-.094l-.034-.07l-.04-.108l-.028-.128l-.012-.102a1 1 0 0 1 0-.125l.012-.1l.024-.11l.045-.122l1.433-3.304l-.009-.014A10 10 0 0 1 5.056 4.83l.215-.203a10 10 0 0 1 13.226-.217M9.5 7.5A1.5 1.5 0 0 0 8 9v1a6 6 0 0 0 6 6h1a1.5 1.5 0 0 0 0-3h-1l-.144.007a1.5 1.5 0 0 0-1.128.697l-.042.074l-.022-.007a4.01 4.01 0 0 1-2.435-2.435l-.008-.023l.075-.041A1.5 1.5 0 0 0 11 10V9a1.5 1.5 0 0 0-1.5-1.5" />
                </svg>
                {lang === "id"
                  ? "Konfirmasi via WhatsApp"
                  : "Confirm via WhatsApp"}
              </button>
            )}

            <br />
            <button
              onClick={() => history.push("/")}
              style={{
                background: "none",
                border: "none",
                color: "#0D8E8C",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "600",
                textDecoration: "underline",
              }}
            >
              {lang === "id" ? "← Kembali ke Beranda" : "← Back to Home"}
            </button>
          </div>
        )}
      </div>

      <Footer lang={lang} />
    </div>
  );
};

export default Booking;
